/**
 * PageSwitcher Component
 *
 * Dropdown selector for switching between pages within a workspace.
 * Uses DropdownMenu from Radix UI for accessible, managed open/close behavior.
 */

import { LayoutGrid, Check, Pencil, Trash2, ChevronDown } from 'lucide-react';
import type { Page } from '@deckspace/types';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';

export interface PageSwitcherProps {
  pages: Page[];
  activePageId: string | null;
  onPageSelect: (pageId: string) => void;
  onRenamePage?: (pageId: string, newName: string) => void;
  onDeletePage?: (pageId: string) => void;
  className?: string;
}

export function PageSwitcher({
  pages,
  activePageId,
  onPageSelect,
  onRenamePage,
  onDeletePage,
  className = '',
}: PageSwitcherProps) {
  const activePage = pages.find((p) => p.id === activePageId);

  if (pages.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-muted hover:bg-muted/80 text-foreground transition-colors ${className}`}
        >
          <LayoutGrid className="h-4 w-4" />
          <span>{activePage?.name ?? 'Select page'}</span>
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {pages.map((page) => (
          <div key={page.id} className="flex items-center gap-1 px-1">
            <DropdownMenuItem className="flex-1" onClick={() => onPageSelect(page.id)}>
              <LayoutGrid className="h-4 w-4" />
              <span className="flex-1 truncate">{page.name}</span>
              {page.id === activePageId && <Check className="h-4 w-4 text-primary shrink-0" />}
            </DropdownMenuItem>
            {onRenamePage && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newName = window.prompt('Rename page', page.name);
                  if (newName && newName.trim()) {
                    onRenamePage(page.id, newName.trim());
                  }
                }}
                className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                title="Rename page"
              >
                <Pencil className="h-3 w-3" />
              </button>
            )}
            {onDeletePage && pages.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeletePage(page.id);
                }}
                className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                title="Delete page"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
